import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { listingsAPI } from '../utils/api';
import { Search, SlidersHorizontal, X, Heart } from 'lucide-react';
import ResponsiveImage from '../components/ResponsiveImage';
import DealerBadge from '../components/DealerBadge';
import TrustedSellerBadge from '../components/TrustedSellerBadge';
import useScrollRestore, { saveFeedScroll } from '../hooks/useScrollRestore';
import { useAuth } from '../context/AuthContext';
import useWishlistToggle from '../hooks/useWishlistToggle';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ minPrice: '', maxPrice: '', sort: 'newest' });
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();
  const { isSaved: isWishlisted, toggle: toggleWishlist } = useWishlistToggle(user);

  // Fix #6: restore scroll on Back from listing detail
  useScrollRestore(`search:${query}`, { ready: results.length > 0 });

  useEffect(() => {
    if (query) loadResults();
    else setLoading(false);
  }, [query]);

  const loadResults = async () => {
    setLoading(true);
    try {
      const res = await listingsAPI.getAll({ search: query });
      setResults(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = results
    .filter(l => {
      if (filters.minPrice && l.price < parseFloat(filters.minPrice)) return false;
      if (filters.maxPrice && l.price > parseFloat(filters.maxPrice)) return false;
      return true;
    })
    .sort((a, b) => {
      if (filters.sort === 'price_low') return a.price - b.price;
      if (filters.sort === 'price_high') return b.price - a.price;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const handleSearch = (e) => {
    e.preventDefault();
    const val = e.target.elements.q.value.trim();
    if (val) setSearchParams({ q: val });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#6B7280] hover:text-[#1800ad] text-sm font-semibold flex-shrink-0" data-testid="back-home">&larr;</Link>
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <input name="q" defaultValue={query} placeholder="Search products, services..."
                className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-[#1800ad] outline-none" data-testid="search-input" />
              <button type="submit" className="bg-[#1800ad] text-white rounded-xl px-4 py-2.5" data-testid="search-btn"><Search size={16} /></button>
            </form>
            <button onClick={() => setShowFilters(!showFilters)} className="p-2.5 rounded-xl bg-gray-100 text-[#6B7280] hover:bg-gray-200" data-testid="filter-toggle">
              <SlidersHorizontal size={18} />
            </button>
          </div>

          {showFilters && (
            <div className="mt-3 flex flex-wrap gap-2 items-center pb-1" data-testid="filters-section">
              <input type="number" placeholder="Min ₹" value={filters.minPrice} onChange={e => setFilters({...filters, minPrice: e.target.value})}
                className="w-24 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" data-testid="min-price" />
              <input type="number" placeholder="Max ₹" value={filters.maxPrice} onChange={e => setFilters({...filters, maxPrice: e.target.value})}
                className="w-24 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" data-testid="max-price" />
              <select value={filters.sort} onChange={e => setFilters({...filters, sort: e.target.value})}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs bg-white" data-testid="sort-select">
                <option value="newest">Newest First</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
              </select>
              <button onClick={() => setFilters({ minPrice: '', maxPrice: '', sort: 'newest' })} className="text-xs text-red-500 font-bold"><X size={14} /></button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6">
        <p className="text-sm text-[#6B7280] mb-4" data-testid="results-count">
          {loading ? 'Searching...' : `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${query}"`}
        </p>

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16">
            <Search size={48} className="mx-auto mb-4 text-[#9CA3AF]" />
            <p className="text-lg font-bold text-[#0A0B10] mb-2">No results found</p>
            <p className="text-sm text-[#6B7280]">Try different keywords or browse categories</p>
            <Link to="/" className="inline-block mt-4 bg-[#1800ad] text-white rounded-full px-6 py-2 text-sm font-bold">Browse Categories</Link>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-5 items-start">
          {filtered.map(listing => (
            <div key={listing.listing_id} className="relative">
              {/* iter118 — shared wishlist heart on every listing card */}
              <button
                type="button"
                onClick={(e) => toggleWishlist(e, listing.listing_id)}
                aria-label={isWishlisted(listing.listing_id) ? 'Remove from wishlist' : 'Add to wishlist'}
                data-testid={`wishlist-toggle-${listing.listing_id}`}
                className="absolute top-2 right-2 z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center hover:bg-white active:scale-90 transition-all"
              >
                <Heart
                  size={17}
                  strokeWidth={2.2}
                  className={isWishlisted(listing.listing_id) ? 'text-red-500 fill-red-500' : 'text-gray-500'}
                />
              </button>
              <Link to={`/listing/${listing.listing_id}`} data-testid={`result-${listing.listing_id}`}
                onClick={() => saveFeedScroll(`search:${query}`)}
                className={`brand-gradient-border-card group flex flex-col rounded-xl sm:rounded-2xl p-2 sm:p-3.5 ${listing.is_dealer_listing ? 'dealer-card' : ''}`}>
                {listing.images?.length > 0 ? (
                  <ResponsiveImage
                    path={listing.images[0]}
                    alt={listing.title}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 25vw"
                    className="w-full h-28 sm:h-44 object-cover rounded-lg sm:rounded-xl mb-2 sm:mb-3"
                  />
                ) : (
                  <div className="w-full h-28 sm:h-44 bg-gray-100 rounded-lg sm:rounded-xl mb-2 sm:mb-3 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No Image</span>
                  </div>
                )}
                <h3 className="text-xs sm:text-base font-bold text-[#0A0B10] mb-1 line-clamp-2">{listing.title}</h3>
                <p className="text-sm sm:text-xl font-bold text-[#1800ad] mb-1">{'\u20B9'}{listing.price?.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-[#9CA3AF]">{listing.location}</p>
                {(listing.is_dealer_listing || listing.is_trusted_seller) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {listing.is_dealer_listing && <DealerBadge testId={`dealer-badge-${listing.listing_id}`} />}
                    {listing.is_trusted_seller && !listing.is_dealer_listing && (
                      <TrustedSellerBadge size="sm" short testId={`trusted-badge-${listing.listing_id}`} />
                    )}
                  </div>
                )}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
